import { useCallback, useMemo, useState } from "react";

import {
    AbfLoanExpanded,
    getZcLedger,
    LoopPositionExpanded,
    StrategyExpandedInfo,
    useStrategySummaries,
    useUnwindQuoteAndIxs,
    useLoopsExpanded,
    getAllowedWindTypes,
    getLoopSlippage,
    useSwapCollateralTransaction,
    useBuildLoanTransactions,
    AbfTransactionDetails,
    getZcCollateral,
    getZcLoanCollateral,
    BsMetaUtil,
    calculateHealthRatio,
    calculateLoanHealth,
    useOraclePrices,
    useTokenListContext,
    findStrategyLtvInfo,
    getTargetStrategyMintDurationTerms,
    strategyIsTargetLoanCompatible,
    getAssetData,
    calculateNetApyLive,
    getHealthStatus,
    calculateCurrentLtv,
    getPandoraErrorMessage,
    LoanCollateralExpanded,
    LedgerExpanded,
    useLoopOraclePriceQuotes,
    useLatestCollateralYieldHistoryByMint,
    getPtTokenTermEnd
} from "@bridgesplit/abf-react";
import {
    Column,
    Row,
    StatColumn,
    StatProps,
    TagTextAlert,
    Text,
    LabelWrapper,
    NestedCard,
    ArrowDivider,
    MEDIA,
    ToggleButtons
} from "@bridgesplit/ui";
import {
    SwapCollateralTxnParams,
    BuildLoanTransactionsRequest,
    TokenListMetadata,
    UncertaintyType,
    DepositCollateralInput,
    OracleQuote
} from "@bridgesplit/abf-sdk";
import { SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";
import { useTransactionSender } from "app/components/transactions/util";
import {
    LOADING_ERROR,
    Result,
    formatDate,
    formatPercent,
    bsMath,
    lamportsToUiAmount,
    uiAmountToLamports
} from "@bridgesplit/utils";
import { COPY } from "app/constants/copy";
import { useMediaQuery } from "@mui/material";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getCpiFromSwapStep, getPrimarySwapInstructionStep, getRouteStepLegs } from "app/utils/swapInstructions";

import { AppButton, Slippage, TokenImage, HealthChange, HealthDetails } from "../../common";
import { getRolloverPtMint, getUnderlyingMint } from "./util";

interface AssetSummary {
    amount: number | undefined;
    assetMetadata: TokenListMetadata | undefined;
    maturityTimestamp: number | undefined;
}
interface RolloverDerivedData {
    newCollateralAmount: number | undefined;
    newCollateralValue: number | undefined;
    currentCollateralPosition: AssetSummary | undefined;
    newCollateralPosition: AssetSummary | undefined;
    newCollateralLqt: number | undefined;
    currentWithdrawPosition: AssetSummary | undefined;
    newWithdrawPosition: AssetSummary | undefined;
    isLoading?: boolean;
}

interface RolloverDataReturn extends RolloverDerivedData {
    rolloverQuoteError: FetchBaseQueryError | SerializedError | undefined;
    firstLedger: LedgerExpanded | undefined;
    firstCollateral: LoanCollateralExpanded | undefined;
    strategy: StrategyExpandedInfo[] | undefined;
    rolloverCollateralMetadata: TokenListMetadata | undefined;
    rolloverCollateralApyPct: number | undefined;
    rolloverQuote: SwapInstructionsResponse | undefined;
    isRolloverElligible: boolean;
}

// Match the loop by principal/collateral pair so manually opened PT positions roll over the same way loops do
function useRolloverLoopPosition(loanExpanded: AbfLoanExpanded | undefined): LoopPositionExpanded | undefined {
    const pairIdentifier = loanExpanded?.loanLoopVaultPairIdentifier;
    const { data: loops } = useLoopsExpanded(
        { loopVaults: pairIdentifier ? [pairIdentifier] : [], showHidden: true },
        { skip: !pairIdentifier }
    );
    const loopExpanded = loops ? Object.values(loops)[0] : undefined;

    return useMemo(() => {
        if (!loanExpanded || !loopExpanded) return undefined;
        return { loanExpanded, loopExpanded, allowedWindTypes: getAllowedWindTypes(loopExpanded) ?? [] };
    }, [loanExpanded, loopExpanded]);
}

function useRolloverData(
    loanExpanded: AbfLoanExpanded | undefined,
    loopPosition: LoopPositionExpanded | undefined,
    slippagePercentDecimals: number | undefined,
    principalOracle: OracleQuote | undefined,
    mode: RolloverMode
): RolloverDataReturn {
    const firstLedger = getZcLedger(loanExpanded);
    const firstCollateral = getZcCollateral(loanExpanded);

    const isRedeem = mode === "redeem";
    // The rest of the hook treats the redeem target (the PT's underlying) as the new collateral.
    const rolloverPtMint = isRedeem
        ? getUnderlyingMint(firstCollateral?.metadata)
        : getRolloverPtMint(firstCollateral?.metadata);
    const { getMetadata } = useTokenListContext();
    const rolloverCollateralMetadata = getMetadata(rolloverPtMint);
    const { data: rolloverCollateralYields } = useLatestCollateralYieldHistoryByMint(
        rolloverPtMint ? [rolloverPtMint] : []
    );
    const rolloverCollateralApyPct = rolloverPtMint ? rolloverCollateralYields?.[rolloverPtMint] : undefined;

    // Summary endpoint with collateralTermsAssetFilter scoped to the rollover's
    // target collateral — that's the only mint `findStrategyLtvInfo` and
    // `strategyIsTargetLoanCompatible` look up below.
    const { data: strategy } = useStrategySummaries(
        {
            addresses: firstLedger?.strategy ? [firstLedger?.strategy] : [],
            collateralTermsAssetFilter: rolloverPtMint ? [rolloverPtMint] : []
        },
        !firstLedger?.strategy || !rolloverPtMint
    );
    const debtToRepay = getZcLedger(loopPosition?.loanExpanded)?.ledgerDebt.total;

    const isRolloverElligible = strategyIsTargetLoanCompatible(
        strategy?.[0],
        loopPosition?.loanExpanded,
        rolloverPtMint
    );

    const loanCollateral = getZcLoanCollateral(loanExpanded);

    const { data: rolloverQuote, error: rolloverQuoteError } = useUnwindQuoteAndIxs({
        slippagePercentDecimals,
        windType: WindType.Principal,
        isRollover: !isRedeem,
        splitRolloverLegs: !isRedeem,
        isCollateralSwap: isRedeem,
        debtToRepay,
        loanCollateral,
        collateralAmount: loanCollateral?.amount,
        loanExpanded: loopPosition?.loanExpanded,
        outputToken: rolloverCollateralMetadata
    });

    const unwindReferenceMint = loopPosition?.loopExpanded?.loopVault.unwindReferenceMint;

    const { getOracle, getUsdPrice } = useOraclePrices([unwindReferenceMint, rolloverPtMint]);

    const derivedData = useMemo(() => {
        if (!rolloverQuote) {
            return {
                newCollateralAmount: undefined,
                newCollateralValue: undefined,
                currentCollateralPosition: undefined,
                newCollateralPosition: undefined,
                newCollateralLqt: undefined,
                currentWithdrawPosition: undefined,
                newWithdrawPosition: undefined,
                isLoading: true
            };
        }

        const redemptionOracle = getOracle(unwindReferenceMint);
        const redemptionPrice = redemptionOracle?.usdPrice ?? principalOracle?.usdPrice;
        const redemptionMetadata = getMetadata(unwindReferenceMint) ?? loopPosition?.loanExpanded.principalMetadata;

        const newCollateralAmount = rolloverQuote?.quote.minOutputAmount;

        const currentCollateral = getZcLoanCollateral(loanExpanded);
        const rolloverMaturityTimestamp = getPtTokenTermEnd(rolloverCollateralMetadata)?.getTime();

        const currentCollateralPosition = {
            amount: currentCollateral?.amount,
            assetMetadata: currentCollateral?.metadata,
            maturityTimestamp: loopPosition?.loopExpanded?.loopVault.maturityTimestamp
        };

        const newCollateralPosition = {
            amount: newCollateralAmount,
            assetMetadata: rolloverCollateralMetadata,
            maturityTimestamp: rolloverMaturityTimestamp ? rolloverMaturityTimestamp / 1000 : undefined
        };

        const newCollateralUsdPrice = getUsdPrice(rolloverPtMint, UncertaintyType.Skip);
        const newCollateralValue = bsMath.mul(
            lamportsToUiAmount(newCollateralAmount, rolloverCollateralMetadata?.decimals),
            newCollateralUsdPrice
        );
        const strategyLtvInfo = findStrategyLtvInfo(strategy?.[0], rolloverPtMint);

        const currentWithdrawPosition = {
            amount: calculateCollateralAmountInRedemptionMint(
                currentCollateral?.amount,
                redemptionPrice, // when redeeming collateral, the asset will be 1:1 with the redemption mint. e.g if the PT is PT-SOL, then 1PT-SOL = 1SOL at redemption
                redemptionPrice
            ),
            assetMetadata: redemptionMetadata,
            maturityTimestamp: loopPosition?.loopExpanded?.loopVault.maturityTimestamp
        };

        const newWithdrawPosition = {
            amount: calculateCollateralAmountInRedemptionMint(
                uiAmountToLamports(
                    lamportsToUiAmount(newCollateralAmount, rolloverCollateralMetadata?.decimals),
                    redemptionMetadata?.decimals
                ),
                redemptionPrice, // when redeeming collateral, the asset will be 1:1 with the redemption mint. e.g if the PT is PT-SOL, then 1PT-SOL = 1SOL at redemption
                redemptionPrice
            ),
            assetMetadata: redemptionMetadata,
            maturityTimestamp: rolloverMaturityTimestamp ? rolloverMaturityTimestamp / 1000 : undefined
        };

        return {
            newCollateralAmount,
            newCollateralValue,
            currentCollateralPosition,
            newCollateralPosition,
            newCollateralLqt: strategyLtvInfo?.liquidationThreshold,
            currentWithdrawPosition,
            newWithdrawPosition
        };
    }, [
        rolloverQuote,
        getOracle,
        unwindReferenceMint,
        principalOracle?.usdPrice,
        loopPosition?.loanExpanded.principalMetadata,
        loopPosition?.loopExpanded?.loopVault.maturityTimestamp,
        getMetadata,
        loanExpanded,
        rolloverCollateralMetadata,
        rolloverPtMint,
        getPtTokenTermEnd,
        getUsdPrice,
        strategy
    ]);

    return {
        firstLedger,
        firstCollateral,
        strategy,
        rolloverCollateralMetadata,
        rolloverCollateralApyPct,
        rolloverQuote,
        rolloverQuoteError,
        isRolloverElligible,
        ...derivedData
    };
}

type RolloverMode = "rollover" | "redeem";

export default function RolloverPt({ loanExpanded }: { loanExpanded: AbfLoanExpanded | undefined }) {
    const loopPosition = useRolloverLoopPosition(loanExpanded);
    const { getMetadata } = useTokenListContext();
    const currentCollateralMetadata = getZcCollateral(loanExpanded)?.metadata;
    const underlyingMetadata = getMetadata(getUnderlyingMint(currentCollateralMetadata));
    const ptTermEnd = getPtTokenTermEnd(currentCollateralMetadata);
    const [mountedAt] = useState(() => Date.now());
    // Before maturity "redeem" would be a market sale, so only offer it once the PT merges 1:1.
    const canRedeem = !!underlyingMetadata && !!ptTermEnd && ptTermEnd.getTime() <= mountedAt;
    const [selectedMode, setMode] = useState<RolloverMode>("rollover");
    const mode = canRedeem ? selectedMode : "rollover";
    const isRedeem = mode === "redeem";
    const send = useTransactionSender();
    const swapCollateral = useSwapCollateralTransaction();
    const loanBuilder = useBuildLoanTransactions(true);
    const { principalOracle } = useLoopOraclePriceQuotes(loopPosition?.loopExpanded);

    const { slippagePresetsUi, defaultSlippageUi } = getLoopSlippage(loopPosition?.loopExpanded);

    const { slippagePercentDecimals, slippageWrapperProps, slippageButtonProps } = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });

    const slippageButton = { ...slippageButtonProps, showSlippageValue: false };

    const {
        firstLedger,
        firstCollateral,
        strategy,
        rolloverCollateralMetadata,
        rolloverCollateralApyPct,
        rolloverQuote,
        rolloverQuoteError,
        isRolloverElligible,
        isLoading,
        currentCollateralPosition,
        newCollateralPosition,
        newCollateralLqt,
        currentWithdrawPosition,
        newWithdrawPosition,
        newCollateralAmount,
        newCollateralValue
    } = useRolloverData(loanExpanded, loopPosition, slippagePercentDecimals, principalOracle, mode);

    const rolloverErrorMessage = useMemo(() => {
        if (!rolloverQuoteError) return undefined;
        return getPandoraErrorMessage(
            rolloverQuoteError,
            "Unable to generate a rollover quote. Please try again later or close your position."
        );
    }, [rolloverQuoteError]);

    const submit = useCallback(async () => {
        const assetData = getAssetData(rolloverCollateralMetadata, rolloverCollateralMetadata?.mint);
        if (
            !loanExpanded ||
            !firstCollateral ||
            !firstLedger ||
            !rolloverCollateralMetadata ||
            !rolloverQuote ||
            newCollateralLqt === undefined ||
            !assetData
        )
            return Result.errFromMessage(LOADING_ERROR);

        const depositParams: DepositCollateralInput = {
            collateralAssetData: assetData,
            collateralAmount: rolloverQuote.quote.minOutputAmount
        };

        const swapInstructions = getPrimarySwapInstructionStep(rolloverQuote);
        // Keep pandora's ATA creates out of the loan lock: they go to the BE setup slot and ride
        // their own tx in the Jito bundle instead of inflating the main (same as LoopUnwind).
        const { cpiIxs, cpiLuts, cpiSigners, setupIxs, setupSigners } = getCpiFromSwapStep(swapInstructions, {
            flattenSetup: false
        });
        const collateralLamports = uiAmountToLamports(firstCollateral.amount, firstCollateral.metadata.decimals);

        const legs = getRouteStepLegs(swapInstructions);
        if (legs.length > 1) {
            // Too big for one tx: one swap_collateral per venue, each built on the loan state the
            // previous one leaves, sent as one bundle. The last leg withdraws whatever is left so
            // the old collateral slot empties.
            let withdrawn = 0;
            const steps: BuildLoanTransactionsRequest["steps"] = legs.map((leg, i) => {
                const amount = i === legs.length - 1 ? collateralLamports - withdrawn : leg.inputAmount;
                withdrawn += amount;
                const params: SwapCollateralTxnParams = {
                    loan: loanExpanded.loan.address,
                    withdrawCollateral: [{ collateralMint: firstCollateral.assetMint, amount }],
                    depositCollateral: [{ collateralAssetData: assetData, collateralAmount: leg.minOutputAmount }],
                    cpiIxs: leg.ixs,
                    cpiLuts,
                    cpiSigners,
                    ...(i === 0 && { setupIxs, setupLuts: cpiLuts, setupSigners }),
                    unifySetup: false
                };
                return { type: "swapCollateral", params };
            });

            const bundledRollover: AbfTransactionDetails<BuildLoanTransactionsRequest> = {
                ...loanBuilder,
                sendOptions: {
                    ...loanBuilder.sendOptions,
                    ...swapCollateral.sendOptions,
                    allowBatchFailure: false,
                    bundle: "always"
                }
            };
            return await send(
                bundledRollover,
                { loanAddress: loanExpanded.loan.address, steps },
                { description: "Rolling over loan" }
            );
        }

        const swapParams: SwapCollateralTxnParams = {
            loan: loanExpanded.loan.address,
            withdrawCollateral: [{ collateralMint: firstCollateral.assetMint, amount: collateralLamports }],
            depositCollateral: [depositParams],
            cpiIxs,
            cpiLuts,
            cpiSigners,
            setupIxs,
            setupLuts: cpiLuts,
            setupSigners,
            unifySetup: false
        };

        return await send(swapCollateral, swapParams, {
            description: "Rolling over loan"
        });
    }, [
        loanExpanded,
        firstCollateral,
        firstLedger,
        rolloverCollateralMetadata,
        rolloverQuote,
        newCollateralLqt,
        send,
        swapCollateral,
        loanBuilder
    ]);

    return (
        <Slippage.Wrapper {...slippageWrapperProps}>
            <Column spacing={2}>
                <Column spacing={1}>
                    <Row spaceBetween>
                        <Text variant="h4">
                            {isRedeem ? `Redeem to ${underlyingMetadata?.symbol ?? "underlying"}` : "Rollover Loop"}
                        </Text>
                        <Row spacing={0.5}>
                            <Slippage.Button {...slippageButton} />
                        </Row>
                    </Row>
                    <Text variant="body2" color="caption">
                        {isRedeem ? COPY.LOOP_REDEEM_DESCRIPTION : COPY.LOOP_ROLLOVER_DESCRIPTION}
                    </Text>
                    {canRedeem && (
                        <ToggleButtons<RolloverMode>
                            size="small"
                            value={mode}
                            setValue={setMode}
                            options={[
                                { value: "rollover", label: "New PT" },
                                { value: "redeem", label: underlyingMetadata?.symbol ?? "Underlying" }
                            ]}
                        />
                    )}
                </Column>
                {rolloverErrorMessage ? (
                    <TagTextAlert color="error" icon="warning" variant="body2">
                        {rolloverErrorMessage}
                    </TagTextAlert>
                ) : (
                    <Column spacing={2}>
                        <Column spacing={2}>
                            <AssetChange
                                label={"New collateral"}
                                newAsset={newCollateralPosition}
                                prevAsset={currentCollateralPosition}
                                isLoading={isLoading}
                            />
                            {!isRedeem && (
                                <AssetChange
                                    label={"Collateral on maturity"}
                                    tooltip={COPY.LOOP_ROLLOVER_WITHDRAW_TOOLTIP}
                                    newAsset={newWithdrawPosition}
                                    prevAsset={currentWithdrawPosition}
                                    isLoading={isLoading}
                                />
                            )}
                        </Column>
                        {isRolloverElligible && (
                            <Stats
                                loading={isLoading}
                                loopPosition={loopPosition}
                                rolloverCollateralMint={rolloverCollateralMetadata?.mint}
                                rolloverCollateralApyPct={rolloverCollateralApyPct}
                                strategy={strategy?.[0]}
                                newCollateralAmount={newCollateralAmount}
                                newCollateralValue={newCollateralValue}
                            />
                        )}
                    </Column>
                )}

                <Column spacing={1}>
                    <AppButton
                        asyncCta={{
                            onClickWithResult: submit,
                            closeDialog: "onSuccess"
                        }}
                        isTransaction
                        disabled={!isRolloverElligible || isLoading || !!rolloverErrorMessage}
                    >
                        {isRedeem ? "Redeem" : "Rollover"}
                    </AppButton>
                    {!isLoading && !isRolloverElligible && (
                        <TagTextAlert color="warning" icon="warning">
                            {COPY.LOOP_ROLLOVER_WARNING_MESSAGE}
                        </TagTextAlert>
                    )}
                </Column>
            </Column>
        </Slippage.Wrapper>
    );
}

function calculateCollateralAmountInRedemptionMint(
    collateralAmount: number | undefined,
    collateralPrice: number | undefined,
    redemptionMintPrice: number | undefined
) {
    return bsMath.div(bsMath.mul(collateralAmount, collateralPrice), redemptionMintPrice);
}

function AssetChange({
    label,
    tooltip,
    newAsset,
    prevAsset,
    isLoading
}: {
    label: string;
    tooltip?: string;
    newAsset: AssetSummary | undefined;
    prevAsset: AssetSummary | undefined;
    isLoading?: boolean;
}) {
    return (
        <LabelWrapper label={label} tooltip={tooltip}>
            <Row>
                <NestedCard position="left" sx={{ flexBasis: 0, flexGrow: 1, p: 1.5, pr: 3 }}>
                    <NestedAssetContent
                        metadata={prevAsset?.assetMetadata}
                        amount={prevAsset?.amount}
                        caption={
                            prevAsset?.maturityTimestamp
                                ? formatDate(prevAsset.maturityTimestamp, "date", {
                                      showTime: "never",
                                      alwaysShowYear: true
                                  })
                                : undefined
                        }
                        disabled
                        loading={isLoading}
                    />
                </NestedCard>
                <ArrowDivider />
                <NestedCard position="right" sx={{ flexBasis: 0, flexGrow: 1, p: 1.5, pl: 3 }}>
                    <NestedAssetContent
                        metadata={newAsset?.assetMetadata}
                        amount={lamportsToUiAmount(newAsset?.amount, newAsset?.assetMetadata?.decimals)}
                        caption={
                            newAsset?.maturityTimestamp
                                ? formatDate(newAsset.maturityTimestamp, "date", {
                                      showTime: "never",
                                      alwaysShowYear: true
                                  })
                                : undefined
                        }
                        loading={isLoading}
                    />
                </NestedCard>
            </Row>
        </LabelWrapper>
    );
}

function NestedAssetContent({
    metadata,
    amount,
    caption,
    disabled,
    loading
}: {
    metadata: TokenListMetadata | undefined;
    amount: number | undefined;
    caption?: string;
    disabled?: boolean;
    loading?: boolean;
}) {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const charLimit = isMobile ? 8 : 15;
    let tokenAmountText = BsMetaUtil.formatAmount(metadata, amount);
    if (tokenAmountText.length > charLimit) {
        tokenAmountText = `${tokenAmountText.slice(0, charLimit)}...`;
    }

    return (
        <Column sx={{ opacity: disabled ? 0.5 : 1 }}>
            <Row spacing={1}>
                {!loading && <TokenImage metadata={metadata} size="sm" />}
                <Text variant="h4" loading={loading}>
                    {tokenAmountText}
                </Text>
            </Row>
            {caption && (
                <Text loading={loading} variant="body2" color="caption">
                    {caption}
                </Text>
            )}
        </Column>
    );
}

function Stats({
    loopPosition,
    rolloverCollateralMint,
    rolloverCollateralApyPct,
    strategy,
    loading,
    newCollateralAmount,
    newCollateralValue
}: {
    loopPosition: LoopPositionExpanded | undefined;
    rolloverCollateralMint: string | undefined;
    rolloverCollateralApyPct: number | undefined;
    strategy: StrategyExpandedInfo | undefined;
    loading?: boolean;
    newCollateralAmount: number | undefined;
    newCollateralValue: number | undefined;
}) {
    const { principalOracle } = useLoopOraclePriceQuotes(loopPosition?.loopExpanded);

    const rolloverStats = getRolloverStats(
        loopPosition,
        rolloverCollateralMint,
        rolloverCollateralApyPct,
        strategy,
        principalOracle,
        newCollateralAmount,
        newCollateralValue
    );

    const stats = useMemo(() => {
        const stats: StatProps[] = [
            {
                caption: "Borrow APY",
                value: [formatPercent(rolloverStats?.currentBorrowApy), formatPercent(rolloverStats?.newBorrowApy)],
                tooltip: COPY.LOOP_ROLLOVER_BORROW_APY_TOOLTIP
            },
            {
                caption: "Net APY",
                value: [formatPercent(rolloverStats?.currentLoopNetApy), formatPercent(rolloverStats?.newLoopNetApy)],
                tooltip: COPY.LOOP_ROLLOVER_NET_APY_TOOLTIP
            },
            {
                value: (
                    <HealthChange
                        variant="body2"
                        originalHealth={rolloverStats?.originalHealth}
                        newHealth={rolloverStats?.newHealth}
                    />
                ),
                tooltip: COPY.LOOP_ROLLOVER_HEALTH_TOOLTIP,
                caption: COPY.HEALTH_FACTOR_TERM
            }
        ];

        return stats;
    }, [rolloverStats]);

    return <StatColumn loading={loading} stats={stats} variant="body2" />;
}

interface RolloverStats {
    currentBorrowApy: number | undefined;
    newBorrowApy: number | undefined;
    currentLoopNetApy: number | undefined;
    newLoopNetApy: number | undefined;
    originalHealth: HealthDetails | undefined;
    newHealth: HealthDetails | undefined;
}

function getRolloverStats(
    loopPosition: LoopPositionExpanded | undefined,
    rolloverCollateralMint: string | undefined,
    rolloverCollateralApyPct: number | undefined,
    newStrategyTerms: StrategyExpandedInfo | undefined,
    principalOracle: OracleQuote | undefined,
    newCollateralAmount: number | undefined,
    newCollateralValue: number | undefined
): RolloverStats | undefined {
    if (!newStrategyTerms) return undefined;
    const ledger = getZcLedger(loopPosition?.loanExpanded);

    const newMintStrategyTerms = getTargetStrategyMintDurationTerms(
        newStrategyTerms,
        loopPosition?.loanExpanded,
        rolloverCollateralMint
    );

    const relevantMetaData = newStrategyTerms.collateral.find((m) => m.metadata.mint === rolloverCollateralMint);

    const newLiquidationThreshold = relevantMetaData?.ltvInfo?.liquidationThreshold;
    const newLtv = relevantMetaData?.ltvInfo?.ltv;

    const loanExpanded = loopPosition?.loanExpanded;

    const { health, loanHealthStatus, ltv: loanLtv } = calculateLoanHealth(loanExpanded);

    const totalDebt = ledger?.ledgerDebt.total;
    const totalDebtUsdValue = bsMath.mul(totalDebt, loanExpanded?.principalUsdPrice);
    const totalNewCollateralUsdValue = newCollateralValue;
    const newBorrowApy = newMintStrategyTerms?.[1];

    const originalHealth: HealthDetails = {
        health,
        loanHealthStatus,
        ltv: loanLtv
    };

    const newLoanLtv = calculateCurrentLtv({
        totalOwedValue: totalDebtUsdValue,
        loanCollateralValue: totalNewCollateralUsdValue
    });

    const newHealth: HealthDetails = {
        health: calculateHealthRatio(totalDebtUsdValue, totalNewCollateralUsdValue, newLiquidationThreshold),
        loanHealthStatus: getHealthStatus(newLoanLtv, newLtv, newLiquidationThreshold),
        ltv: newLoanLtv
    };

    const newLoopNetApy = calculateNetApyLive({
        loanCollateral: getZcLoanCollateral(loopPosition?.loanExpanded),
        collateralApyPct: rolloverCollateralApyPct,
        principalApyPct: newBorrowApy,
        collateralUsdValue: totalNewCollateralUsdValue,
        totalDebtUsdValue,
        principalYieldPct: loopPosition?.loanExpanded?.principalYieldPct
    });

    const currentBorrowApy = loopPosition?.loanExpanded?.borrowerWAvgApy;

    return {
        currentBorrowApy,
        newBorrowApy,
        currentLoopNetApy: loopPosition?.loanExpanded?.netApyLive,
        newLoopNetApy,
        originalHealth,
        newHealth
    };
}
